/**
 * Wire shapes for postage on mailed payment requests (GH-846, DEC-048).
 *
 * The organization pays postage: from its balance first, then from a card it keeps on file for
 * postage. These are the console's view of that — the per-letter price shown before a letter is
 * sent, the card on file (brand, last four, expiry; never a card number, which this platform never
 * receives), and the two-step add: a SetupIntent the browser confirms with Stripe Elements, then
 * the setup id handed back to be saved.
 */
import { z } from "zod";
import { controlChangeReasonSchema, moneySchema } from "./v1.js";
export const postageCardSchema = z.object({
    brand: z.string(),
    last4: z.string().regex(/^\d{4}$/),
    expMonth: z.number().int().min(1).max(12),
    expYear: z.number().int(),
    addedAt: z.string(),
});
export const postageSummarySchema = z.object({
    /** What one letter costs the organization, shown before it is sent. */
    postagePerLetter: moneySchema,
    card: postageCardSchema.nullable(),
    /** Whether the organization's available balance covers one letter right now. */
    balanceCoversOneLetter: z.boolean(),
    /** False where this deployment cannot take a card (no processor configured for billing). */
    cardBillingAvailable: z.boolean(),
    /** Whether the caller may add or remove the card (owner and finance reconciler). */
    canManageCard: z.boolean(),
});
export const postageCardSetupSchema = z.object({
    setupIntentId: z.string().min(1),
    /** Handed to Stripe Elements in the browser. Not a secret key; scoped to this one setup. */
    clientSecret: z.string().min(1),
});
export const savePostageCardRequestSchema = z
    .object({ setupIntentId: z.string().trim().min(1).max(255) })
    .strict();
export const removePostageCardResponseSchema = z.object({ removed: z.boolean() });
const PER_LETTER_CENTS = /^\d{1,4}$/;
/** Platform staff set the per-letter price, behind step-up and a reason (like the fee schedule). */
export const updatePostagePriceSchema = z
    .object({
    perLetterMinor: z.string().regex(PER_LETTER_CENTS),
    reason: controlChangeReasonSchema,
})
    .strict();
export const postagePriceSchema = z.object({ postagePerLetter: moneySchema });
//# sourceMappingURL=postage.js.map